import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { CheckCircle2, FileCode2, FileText, RotateCw } from 'lucide-react';
import { apiErrorMessage, marketingSiteService } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';
import { ErrorState } from '../../components/PageState';
import BulkReviewModal from './BulkReviewModal';
import { SeverityIcon } from './SeoFindings';
import { shortSha } from './ui';

const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

/** The markdown line the "insert link" helper writes, in llms.txt's existing style. */
export const llmsLine = (page) => `- [${page.title || page.url}](${page.loc}${page.url === '/' ? '/' : ''})${page.description ? `: ${page.description}` : ''}`;

const SitemapCard = ({ status, canEdit, siteUrl, onOpenPage, onChanged }) => {
  const [review, setReview] = useState(null); // { sort }
  const sitemap = status.sitemap;
  const upToDate = sitemap.missing.length === 0 && sitemap.stale.length === 0;
  const loadPreview = useCallback(async () => (await marketingSiteService.sitemapPreview({ sort: review?.sort })).data, [review]);
  const apply = useCallback(
    async ({ baseSha, message }) =>
      (await marketingSiteService.sitemapApply({ params: { sort: review?.sort }, baseSha, message })).data,
    [review]
  );

  return (
    <section className="mse-index-card">
      <h6 className="mse-section-title">
        <FileCode2 size={14} /> sitemap.xml
      </h6>
      {!sitemap.present ? (
        <Alert variant="warning">The site has no sitemap.xml.</Alert>
      ) : (
        <>
          <p className="mb-2">
            {upToDate ? (
              <span className="d-inline-flex align-items-center gap-1">
                <CheckCircle2 size={14} className="text-success" /> Up to date: {plural(sitemap.entries, 'page')} listed.
              </span>
            ) : (
              <strong>
                {[
                  sitemap.missing.length > 0 && `${plural(sitemap.missing.length, 'page')} missing`,
                  sitemap.stale.length > 0 && `${plural(sitemap.stale.length, 'entry', 'entries')} to remove`,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </strong>
            )}
            <span className="mse-hint">
              {' '}
              {plural(sitemap.noindexPages, 'page')} marked noindex {sitemap.noindexPages === 1 ? 'is' : 'are'} left out on purpose.
            </span>
          </p>
          {sitemap.missing.length > 0 && (
            <ul className="mse-index-list">
              {sitemap.missing.map((p) => (
                <li key={p.path}>
                  <SeverityIcon severity="WARN" size={12} />{' '}
                  <button type="button" className="mse-link-button mse-mono" onClick={() => onOpenPage(p.path)}>
                    {p.url}
                  </button>{' '}
                  <span className="mse-hint">not listed</span>
                </li>
              ))}
            </ul>
          )}
          {sitemap.stale.length > 0 && (
            <ul className="mse-index-list">
              {sitemap.stale.map((e) => (
                <li key={e.loc}>
                  <SeverityIcon severity="WARN" size={12} /> <code>{e.loc}</code> <span className="mse-hint">{e.reason}</span>
                </li>
              ))}
            </ul>
          )}
          {canEdit && (
            <div className="d-flex gap-2 mt-2">
              <Button
                size="sm"
                variant="primary"
                disabled={upToDate}
                onClick={() => setReview({ sort: false })}
              >
                Update sitemap
              </Button>
              {!sitemap.sorted && (
                <Button size="sm" variant="outline-secondary" onClick={() => setReview({ sort: true })} title="Rewrites every entry in order">
                  Sort alphabetically
                </Button>
              )}
            </div>
          )}
          <p className="mse-hint mt-2 mb-0">
            Updating adds missing pages next to their neighbours and removes dead entries; the rest of the file is left as it is.
          </p>
        </>
      )}
      {review && (
        <BulkReviewModal
          show={!!review}
          title={review.sort ? 'Sort sitemap.xml' : 'Update sitemap.xml'}
          applyLabel="Save to test site"
          siteUrl={siteUrl}
          loadPreview={loadPreview}
          apply={apply}
          onClose={() => setReview(null)}
          onApplied={onChanged}
        />
      )}
    </section>
  );
};

const LlmsCard = ({ status, canEdit, siteUrl, onChanged, onDraftChange }) => {
  const llms = status.llms;
  const [text, setText] = useState(llms.content);
  const [review, setReview] = useState(false);
  const [showUnlisted, setShowUnlisted] = useState(false);
  const [filter, setFilter] = useState('');
  const area = useRef(null);
  const dirty = text !== llms.content;

  useEffect(() => setText(llms.content), [llms.content, llms.sha]);

  // An unsaved draft counts as unsaved work: the editor asks before anything navigates away from it.
  useEffect(() => {
    onDraftChange?.(dirty);
  }, [dirty, onDraftChange]);
  useEffect(() => () => onDraftChange?.(false), [onDraftChange]);

  const insert = (line) => {
    const el = area.current;
    const at = el ? el.selectionStart : text.length;
    let lineStart = text.lastIndexOf('\n', at - 1) + 1;
    // On a heading or blank line, the link belongs at the end of the list above, not between it and the heading.
    const current = text.slice(lineStart, text.indexOf('\n', lineStart) < 0 ? undefined : text.indexOf('\n', lineStart));
    if (lineStart > 0 && (current.startsWith('#') || current.trim() === '')) {
      while (lineStart > 0) {
        const prevStart = text.lastIndexOf('\n', lineStart - 2) + 1;
        if (text.slice(prevStart, lineStart - 1).trim() !== '') break;
        lineStart = prevStart;
      }
    }
    const next = `${text.slice(0, lineStart)}${line}\n${text.slice(lineStart)}`;
    setText(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.selectionStart = el.selectionEnd = lineStart + line.length + 1;
    });
  };

  const applyFix = (problem) => setText((t) => t.split(problem.url).join(problem.fix));

  const unlisted = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return llms.unlisted.filter((p) => !q || p.url.includes(q) || (p.title || '').toLowerCase().includes(q));
  }, [llms.unlisted, filter]);

  const loadPreview = useCallback(async () => (await marketingSiteService.llmsPreview({ content: text, baseSha: llms.sha })).data, [text, llms.sha]);
  const apply = useCallback(
    async ({ baseSha, message }) =>
      (await marketingSiteService.llmsApply({ params: { content: text, baseSha: llms.sha }, baseSha, message })).data,
    [text, llms.sha]
  );

  if (!llms.present) {
    return (
      <section className="mse-index-card">
        <h6 className="mse-section-title">
          <FileText size={14} /> llms.txt
        </h6>
        <Alert variant="secondary">The site has no llms.txt.</Alert>
      </section>
    );
  }

  return (
    <section className="mse-index-card">
      <h6 className="mse-section-title">
        <FileText size={14} /> llms.txt
        <span className="mse-hint ms-2">the curated site guide AI assistants read</span>
      </h6>
      <p className="mb-2">
        {llms.problems.length === 0 ? (
          <span className="d-inline-flex align-items-center gap-1">
            <CheckCircle2 size={14} className="text-success" /> All {llms.links} links work.
          </span>
        ) : (
          <strong>{plural(llms.problems.length, 'link needs', 'links need')} attention</strong>
        )}
      </p>
      {llms.problems.length > 0 && (
        <ul className="mse-index-list">
          {llms.problems.map((p) => (
            <li key={`${p.line}-${p.url}`}>
              <SeverityIcon severity={p.fix ? 'INFO' : 'ERROR'} size={12} /> Line {p.line}: <code>{p.url}</code>{' '}
              <span className="mse-hint">{p.problem}</span>
              {canEdit && p.fix && text.includes(p.url) && (
                <button type="button" className="mse-link-button ms-2" onClick={() => applyFix(p)}>
                  Use {p.fix.replace(status.canonicalOrigin, '')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <Form.Control
        as="textarea"
        ref={area}
        className="mse-mono mse-llms-text"
        rows={16}
        value={text}
        readOnly={!canEdit}
        spellCheck={false}
        onChange={(e) => setText(e.target.value)}
        aria-label="llms.txt"
      />
      {canEdit && (
        <div className="d-flex align-items-center gap-2 mt-2">
          <Button size="sm" variant="primary" disabled={!dirty} onClick={() => setReview(true)}>
            Save llms.txt
          </Button>
          {dirty && (
            <Button size="sm" variant="outline-secondary" onClick={() => setText(llms.content)}>
              Revert
            </Button>
          )}
          <button type="button" className="mse-link-button ms-auto" onClick={() => setShowUnlisted((v) => !v)}>
            {showUnlisted ? 'Hide' : 'Add a page'} ({llms.unlisted.length} not listed)
          </button>
        </div>
      )}
      {canEdit && showUnlisted && (
        <div className="mse-llms-unlisted">
          <p className="mse-hint mb-1">
            llms.txt is a short guide, not a full index; add the pages an assistant should know about. The link goes in above
            the line your cursor is on.
          </p>
          <Form.Control
            size="sm"
            type="search"
            placeholder="Filter pages"
            aria-label="Filter pages not in llms.txt"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <ul className="mse-index-list">
            {unlisted.slice(0, 50).map((p) => (
              <li key={p.path}>
                <button type="button" className="mse-link-button" onClick={() => insert(llmsLine(p))} title={llmsLine(p)}>
                  Insert
                </button>{' '}
                <code>{p.url}</code> <span className="mse-hint">{p.title}</span>
              </li>
            ))}
            {unlisted.length > 50 && <li className="mse-hint">and {unlisted.length - 50} more; filter to find them</li>}
          </ul>
        </div>
      )}
      {review && (
        <BulkReviewModal
          show={review}
          title="Save llms.txt"
          applyLabel="Save to test site"
          siteUrl={siteUrl}
          loadPreview={loadPreview}
          apply={apply}
          onClose={() => setReview(false)}
          onApplied={onChanged}
        />
      )}
    </section>
  );
};

/** sitemap.xml and llms.txt against the site's pages. */
const IndexReport = ({ canEdit, siteUrl, onOpenPage, onStatus, onSaved, onDraftChange }) => {
  const ensureToken = useAuthToken();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await ensureToken();
      const data = (await marketingSiteService.indexStatus()).data;
      setStatus(data);
      onStatus?.(data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not check the sitemap and llms.txt.'));
    } finally {
      setLoading(false);
    }
  }, [ensureToken, onStatus]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !status) {
    return (
      <div className="mse-empty">
        <Spinner animation="border" size="sm" /> Checking sitemap.xml and llms.txt
      </div>
    );
  }
  // A failed recheck keeps the cards (and any llms.txt draft) on screen under the error
  if (error && !status) return <ErrorState message={error} onRetry={load} />;
  if (!status) return null;
  const changed = () => {
    load();
    onSaved?.();
  };

  return (
    <div className="mse-health-report">
      <div className="mse-health-summary">
        <span className="mse-hint">
          Checked against the test site at <code>{shortSha(status.headSha)}</code>
        </span>
        <Button
          size="sm"
          variant="outline-secondary"
          className="ms-auto"
          onClick={load}
          disabled={loading}
          title="Check again"
          aria-label="Check again"
        >
          {loading ? <Spinner animation="border" size="sm" /> : <RotateCw size={14} />}
        </Button>
      </div>
      {error && <ErrorState message={error} onRetry={load} />}
      <SitemapCard status={status} canEdit={canEdit} siteUrl={siteUrl} onOpenPage={onOpenPage} onChanged={changed} />
      <LlmsCard status={status} canEdit={canEdit} siteUrl={siteUrl} onChanged={changed} onDraftChange={onDraftChange} />
    </div>
  );
};

export default IndexReport;
